import { t as $t } from '@enercad/core';

/*
 * Manufacturer 3D models: STEP / IGES files are tessellated in the browser
 * with occt-import-js (OpenCascade compiled to WebAssembly, LGPL-2.1, loaded
 * only here) and stored as binary glTF. The orientation maps model axes to
 * the device's width, height and depth.
 */

export interface ModelOrient {
  /** model axis (0 x, 1 y, 2 z) along the device width, height and depth */
  perm: [number, number, number];
  /** turned around: the model's back faces the door */
  flip?: boolean;
}

/** Reads a STEP / IGES / glb file and returns a glb blob. */
export async function modelToGlb(file: File): Promise<Blob> {
  const buf = new Uint8Array(await file.arrayBuffer());
  if (/\.glb$/i.test(file.name)) return new Blob([buf], { type: 'model/gltf-binary' });
  const iges = /\.(igs|iges)$/i.test(file.name);
  if (!iges && !/\.(stp|step)$/i.test(file.name)) throw new Error($t('3D model STEP (.step, .stp), IGES ya da .glb olmalı.'));
  const [occtMod, wasm, THREE, { GLTFExporter }] = await Promise.all([
    import('occt-import-js'),
    import('occt-import-js/dist/occt-import-js.wasm?url'),
    import('three'),
    import('three/examples/jsm/exporters/GLTFExporter.js'),
  ]);
  const factory = ((occtMod as any).default ?? occtMod) as (o: object) => Promise<any>;
  const occt = await factory({ locateFile: (n: string) => (n.endsWith('.wasm') ? wasm.default : n) });
  const params = { linearUnit: 'millimeter', linearDeflectionType: 'bounding_box_ratio', linearDeflection: 0.003, angularDeflection: 0.6 };
  const res = iges ? occt.ReadIgesFile(buf, params) : occt.ReadStepFile(buf, params);
  if (!res?.success || !res.meshes?.length) throw new Error($t('Dosya okunamadı ya da içinde katı model yok.'));
  const group = new THREE.Group();
  for (const m of res.meshes) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(m.attributes.position.array, 3));
    if (m.attributes.normal) g.setAttribute('normal', new THREE.Float32BufferAttribute(m.attributes.normal.array, 3));
    else g.computeVertexNormals();
    if (m.index) g.setIndex(m.index.array);
    const c = m.color ? new THREE.Color(m.color[0], m.color[1], m.color[2]) : new THREE.Color(0x9aa0a8);
    group.add(new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: c, roughness: 0.6, metalness: 0.1 })));
  }
  const out = await new Promise<ArrayBuffer>((ok, bad) => new GLTFExporter().parse(group, (r) => ok(r as ArrayBuffer), bad, { binary: true }));
  return new Blob([out], { type: 'model/gltf-binary' });
}

/** Model size along its own axes, from a glb (mm). */
export async function glbSize(blob: Blob): Promise<[number, number, number]> {
  const [THREE, { GLTFLoader }] = await Promise.all([import('three'), import('three/examples/jsm/loaders/GLTFLoader.js')]);
  const gltf = await new GLTFLoader().parseAsync(await blob.arrayBuffer(), '');
  const s = new THREE.Box3().setFromObject(gltf.scene).getSize(new THREE.Vector3());
  return [s.x, s.y, s.z];
}

/**
 * Picks the axes that best match the part's width, height and depth; without
 * part dimensions CAD's usual orientation is assumed (Z up, looking along +Y).
 */
export function autoOrient(size: [number, number, number], part: { w?: number; h?: number; dp?: number }): ModelOrient {
  const want = [part.w, part.h, part.dp];
  if (want.some((v) => !v)) return { perm: [0, 2, 1] };
  const perms: [number, number, number][] = [[0, 1, 2], [0, 2, 1], [1, 0, 2], [1, 2, 0], [2, 0, 1], [2, 1, 0]];
  const err = (p: number[]) => p.reduce((a, ax, i) => a + Math.abs(size[ax] - want[i]!) / want[i]!, 0);
  return { perm: perms.sort((a, b) => err(a) - err(b))[0] };
}

/** Rotation (as a column-major 3×3 basis) turning model axes into device width, height, depth (x right, y up, z to the door). */
export function orientBasis(o: ModelOrient): number[] {
  // rows of the rotation: scene axis i takes model axis perm[i] with a sign
  const rows: number[][] = o.perm.map((ax) => [0, 1, 2].map((k) => (k === ax ? 1 : 0)));
  // a proper rotation: the CAD default (x, z, -y) and every odd permutation need one negated axis
  const det = (m: number[][]) =>
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  if (det(rows) < 0) rows[2] = rows[2].map((v) => -v);
  if (o.flip) {
    rows[0] = rows[0].map((v) => -v);
    rows[2] = rows[2].map((v) => -v);
  }
  // column-major for THREE.Matrix4.set via makeBasis-like use: return rows flattened
  return rows.flat();
}
